import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as IconChevron } from 'assets/icons/svgs/icon-right-chevron.svg';
import { isValid as dateIsValid } from 'date-fns';
import { asYearMonthDay, getToday, getDayNameAbbr, getNumberInMonth } from 'utils/date-formatter';
import { scrollSection, getCurrentMonthOnScroll } from './date-picker-utils';

export const DateSelector = (props) => {
  const {
    years,
    selectedDate,
    setDateHandler,
    dateSwiperWrapRef,
    dateSwiperRef,
    calendarRef,
    setSelectedMonth,
  } = props;
  const [dateNavVisible, setDateNavVisible] = useState(false);
  const [datePrevDisabled, setDatePrevDisabled] = useState(true);
  const [dateNextDisabled, setDateNextDisabled] = useState(false);
  const [scrollAmount, setScrollAmount] = useState(0);
  const navLeftBtnRef = useRef();

  const currentDate = getToday();
  const currentDateValueGeneral = asYearMonthDay(currentDate);

  const dateButtonsClickHandler = (event, date) => {
    setDateHandler(date);
    event.preventDefault();
  };

  // Select the month corresponding to the days displayed in the viewport
  const onDateScrollHandler = () => {
    const currentMonth = getCurrentMonthOnScroll(calendarRef.current, navLeftBtnRef.current);

    if (currentMonth) {
      setSelectedMonth(currentMonth);
    }

    const dateSwiper = dateSwiperRef.current;
    const dateButtonsMargin = 1;

    setDatePrevDisabled(dateSwiper.scrollLeft - scrollAmount - dateButtonsMargin <= 0);
    setDateNextDisabled(
      Math.round(dateSwiper.scrollLeft) + dateButtonsMargin >=
        dateSwiper.scrollWidth - dateSwiper.offsetWidth
    );
  };

  const navLeftBtnClickHandler = () => {
    const swiperRef = dateSwiperRef.current;
    scrollSection({
      swiperType: 'date',
      swiperRef,
      direction: 'left',
      scrollAmount,
    });

    setDateNextDisabled(false);
  };

  const navRightBtnClickHandler = () => {
    const swiperRef = dateSwiperRef.current;
    scrollSection({
      swiperType: 'date',
      swiperRef,
      direction: 'right',
      scrollAmount,
    });

    setDatePrevDisabled(false);
  };

  const handleNavButtonsDisplay = () => {
    const swiperWrap = dateSwiperWrapRef.current;
    const swiper = dateSwiperRef.current;

    setDateNavVisible(swiper.scrollWidth > swiperWrap.scrollWidth);
  };

  useEffect(() => {
    handleNavButtonsDisplay();
    setScrollAmount(Math.floor((calendarRef.current.offsetWidth * 65) / 100));

    dateSwiperRef.current.addEventListener('scroll', onDateScrollHandler);
    window.addEventListener('resize', handleNavButtonsDisplay);
    return () => window.removeEventListener('resize', handleNavButtonsDisplay);
  }, []);

  return (
    <div className="swiper-wrap date-selector" ref={dateSwiperWrapRef} data-selector="date">
      {dateNavVisible && (
        <button
          type="button"
          className={`btn calendar-nav-btn btn-prev1 ${datePrevDisabled ? 'disabled' : ''}`}
          onClick={navLeftBtnClickHandler}
          disabled={datePrevDisabled}
          ref={navLeftBtnRef}
        >
          <IconChevron className="icon left-chevron" />
          <span className="sr-only">Previous month</span>
        </button>
      )}

      <div
        className={`swiper date-swiper ${dateNavVisible ? 'nav-visible' : ''}`}
        ref={dateSwiperRef}
      >
        {years?.map((year, yearIndex) => (
          <div className="range-wrap" key={yearIndex}>
            {year.Months?.map((month, monthIndex) => (
              <ul className="range month-display" id={`${month.Identifier}`} key={monthIndex}>
                {month.Days?.map((date, index) => {
                  const dateValueGeneral = asYearMonthDay(date.DateValue);
                  const dayNameAbbr = getDayNameAbbr(dateValueGeneral);
                  const numberInMonth = getNumberInMonth(dateValueGeneral);

                  const selectedClass =
                    dateIsValid(Date.parse(selectedDate)) &&
                    dateValueGeneral === asYearMonthDay(selectedDate)
                      ? 'selected'
                      : '';
                  const currentDateClass =
                    dateValueGeneral === currentDateValueGeneral ? 'current-date' : '';
                  const dayClasses = [selectedClass, currentDateClass].join(' ');

                  return (
                    <li key={index}>
                      {date.Active ? (
                        <a
                          href="#"
                          className={`date-element ${dayClasses}`}
                          onClick={(event) => dateButtonsClickHandler(event, dateValueGeneral)}
                        >
                          <span className="day">{dayNameAbbr}</span>
                          <span className="date">{numberInMonth}</span>
                        </a>
                      ) : (
                        <span
                          className={`date-element unavailable ${dayClasses}`}
                          onClick={(event) => event.preventDefault()}
                        >
                          <span className="day">{dayNameAbbr}</span>
                          <span className="date">{numberInMonth}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            ))}
          </div>
        ))}
      </div>

      {dateNavVisible && (
        <button
          type="button"
          className={`btn calendar-nav-btn btn-prev1 ${dateNextDisabled ? 'disabled' : ''}`}
          onClick={navRightBtnClickHandler}
          disabled={dateNextDisabled}
        >
          <IconChevron className="icon" />
          <span className="sr-only">Previous month</span>
        </button>
      )}
    </div>
  );
};

DateSelector.propTypes = {
  years: PropTypes.array,
  selectedDate: PropTypes.string,
  setDateHandler: PropTypes.func,
  dateSwiperWrapRef: PropTypes.object,
  dateSwiperRef: PropTypes.object,
  calendarRef: PropTypes.object,
  setSelectedMonth: PropTypes.func,
};
